import {
  startOfDay,
  endOfDay,
  subDays,
  startOfMonth,
  endOfMonth,
  subMonths,
} from 'date-fns';
import type { DateRangePreset, DateRangeValue } from '@/components/ui/date-range-select';

const now = () => new Date();

// Registry of reusable date-range presets. Keys are kept stable so existing
// callers that persisted a preset key keep working.
export const DATE_PRESETS = {
  today: {
    key: 'today',
    label: 'Today',
    getRange: () => ({ start: startOfDay(now()), end: endOfDay(now()) }),
  },
  yesterday: {
    key: 'yesterday',
    label: 'Yesterday',
    getRange: () => ({ start: startOfDay(subDays(now(), 1)), end: endOfDay(subDays(now(), 1)) }),
  },
  week: {
    key: 'week',
    label: 'Last 7 Days',
    getRange: () => ({ start: startOfDay(subDays(now(), 6)), end: endOfDay(now()) }),
  },
  last30: {
    key: 'last30',
    label: 'Last 30 Days',
    getRange: () => ({ start: startOfDay(subDays(now(), 29)), end: endOfDay(now()) }),
  },
  last90: {
    key: 'last90',
    label: 'Last 90 Days',
    getRange: () => ({ start: startOfDay(subDays(now(), 89)), end: endOfDay(now()) }),
  },
  month: {
    key: 'month',
    label: 'This Month',
    getRange: () => ({ start: startOfMonth(now()), end: endOfDay(now()) }),
  },
  last_month: {
    key: 'last_month',
    label: 'Last Month',
    getRange: () => ({
      start: startOfMonth(subMonths(now(), 1)),
      end: endOfMonth(subMonths(now(), 1)),
    }),
  },
  year: {
    key: 'year',
    label: 'This Year',
    getRange: () => ({ start: new Date(now().getFullYear(), 0, 1), end: endOfDay(now()) }),
  },
  all_time: {
    key: 'all_time',
    label: 'All Time',
    getRange: () => ({ start: new Date(2000, 0, 1), end: endOfDay(now()) }),
  },
} satisfies Record<string, DateRangePreset>;

export type DatePresetKey = keyof typeof DATE_PRESETS;

/** Build a preset list (in order) for the DateRangeSelect component. */
export function datePresets(...keys: DatePresetKey[]): DateRangePreset[] {
  return keys.map((k) => DATE_PRESETS[k]);
}

/** Build the initial DateRangeValue for a given preset key. */
export function presetValue(key: DatePresetKey): DateRangeValue {
  const preset = DATE_PRESETS[key];
  const range = preset.getRange();
  return { preset: preset.key, start: range.start, end: range.end };
}
